import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardYieldWeightCardInfo,
    GrowthDashboardReq,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthYieldWeightInfoCardProps {
    gfIdx: number;
}

const GrowthYieldWeightInfoCard = ({
    gfIdx,
}: GrowthYieldWeightInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [yieldWeightInfo, setYieldWeightInfo] =
        useState<GrowthDashboardYieldWeightCardInfo | null>(null);

    // 예측수확량 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchYieldWeightInfo(gfIdx);
        }
    }, [gfIdx]);

    const fetchYieldWeightInfo = async (gfIdx: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getYieldWeightCardInfo(req);
            setYieldWeightInfo(response.data);
        } catch (error) {
            console.error('예측수확량 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (
            !chartRef.current ||
            !yieldWeightInfo ||
            yieldWeightInfo.list.length === 0
        )
            return;

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // 예측수확량 값 배열 추출
        const yieldWeightValues = yieldWeightInfo.list.map(
            item => item.yieldWeight,
        );

        const options = {
            series: [
                {
                    name: '예측수확량',
                    data: yieldWeightValues,
                },
            ],
            chart: {
                type: 'area',
                height: 70,
                sparkline: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 0,
                    left: 0,
                    blur: 3,
                    color: 'rgba(var(--success-rgb))',
                    opacity: 0.4,
                },
            },
            stroke: {
                curve: 'smooth', // 곡선
                width: 1,
            },
            fill: {
                type: 'gradient',
                gradient: {
                    opacityFrom: 0.5,
                    opacityTo: 0.2,
                    stops: [0, 60],
                    colorStops: [
                        [
                            {
                                offset: 0,
                                color: 'rgba(var(--success-rgb))',
                                opacity: 0.3,
                            },
                            {
                                offset: 60,
                                color: 'rgba(var(--success-rgb))',
                                opacity: 0.2,
                            },
                        ],
                    ],
                },
            },
            colors: ['rgba(var(--success-rgb))'],
            tooltip: {
                fixed: {
                    enabled: false,
                },
                x: {
                    show: false,
                },
                y: {
                    title: {
                        formatter: function () {
                            return '';
                        },
                    },
                    formatter: function (value: number) {
                        return value.toFixed(0) + 'kg';
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [yieldWeightInfo]);

    // 차이 표시 계산 (증가/감소)
    const getDifferenceDisplay = () => {
        if (!yieldWeightInfo || yieldWeightInfo.yieldWeightDifference === null)
            return null;

        const diff = yieldWeightInfo.yieldWeightDifference;
        const isPositive = diff >= 0;
        const icon = isPositive
            ? 'ri-arrow-left-up-line'
            : 'ri-arrow-left-down-line';

        return (
            <span className="badge bg-success rounded-pill align-items-center fs-11">
                <i className={`${icon} fs-11`}></i> {Math.abs(diff).toFixed(0)}
                kg
            </span>
        );
    };

    if (loading) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-center">
                        <div
                            className="spinner-border text-success"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!yieldWeightInfo) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="text-center text-muted">
                        예측수확량 데이터를 불러올 수 없습니다
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card overflow-hidden">
            <div className="">
                <div className="card-body">
                    <div className="d-flex align-items-center w-100 justify-content-between gap-1">
                        <div>
                            <p className="mb-1 text-muted fw-medium">
                                평균 예측수확량
                                <Tooltip
                                    content="예측수확량은 현재까지의 생육 데이터를 바탕으로 추정한 수확 시점의 예상 수확량입니다. 생육 환경 변화에 따라 값이 달라질 수 있습니다."
                                    customClass="tooltip-dark"
                                    placement="top"
                                >
                                    <i className="ri-question-line" />
                                </Tooltip>
                            </p>
                            <h4 className="mb-0 fw-medium">
                                {yieldWeightInfo.recentAvgYieldWeight
                                    ? yieldWeightInfo.recentAvgYieldWeight.toFixed(
                                          0,
                                      )
                                    : '0'}
                                <small className="fs-11">kg</small>
                            </h4>
                        </div>
                        <div className="ms-auto text-end">
                            {getDifferenceDisplay()}
                            <div className="text-muted fs-12 mt-1">
                                2주전 대비
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    ref={chartRef}
                    id="yield-weight-chart"
                    style={{
                        marginTop: -20,
                    }}
                ></div>
            </div>
        </div>
    );
};

export default GrowthYieldWeightInfoCard;
